import { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Upload, FileText, FileSpreadsheet, File as FileIcon, CheckCircle2, AlertCircle, Loader2,
  X, RefreshCw, Eye, Plus, ArrowRight, AlertTriangle, DatabaseZap, ExternalLink, BrainCircuit
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  idle: { label: "Ready", color: "text-slate-500", bg: "bg-slate-500/10" },
  uploading: { label: "Uploading...", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  parsing: { label: "Parsing...", color: "text-amber-400", bg: "bg-amber-500/10" },
  classified: { label: "Classified", color: "text-blue-400", bg: "bg-blue-500/10" },
  needs_review: { label: "Needs Review", color: "text-amber-400", bg: "bg-amber-500/10" },
  ready: { label: "Ready to Import", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  importing: { label: "Importing...", color: "text-cyan-400", bg: "bg-cyan-500/10" },
  imported: { label: "Imported", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  failed: { label: "Failed", color: "text-red-400", bg: "bg-red-500/10" },
};

const FILE_ICONS = {
  pdf: FileText, csv: FileSpreadsheet, xlsx: FileSpreadsheet, xls: FileSpreadsheet,
  json: File, tsv: FileSpreadsheet, txt: FileText, docx: FileText,
};

export default function IngestPage() {
  const { token, formatMoney } = useAuth();
  const navigate = useNavigate();
  const headers = { Authorization: `Bearer ${token}` };

  // Session persistence: restore file metadata from localStorage (not file objects)
  const [files, setFiles] = useState(() => {
    try {
      const saved = localStorage.getItem("sv_ingest_files");
      if (saved) {
        const parsed = JSON.parse(saved);
        // Only restore non-transient states (uploaded/ready/imported/failed)
        return parsed.filter(f => ["ready", "needs_review", "imported", "failed"].includes(f.status));
      }
    } catch {}
    return [];
  });
  const [activeView, setActiveView] = useState("upload");
  const [reviewFile, setReviewFile] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualType, setManualType] = useState("invoice");
  const [manualForm, setManualForm] = useState({ counterparty: "", amount: "", tax_amount: "0", reference: "", due_date: "", description: "" });
  const [manualLoading, setManualLoading] = useState(false);
  const [manualResult, setManualResult] = useState(null);
  const fileInputRef = useRef(null);

  // Persist file metadata (without file blobs) to localStorage
  const persistFiles = (fileList) => {
    try {
      const serializable = fileList.map(({ file, ...rest }) => rest);
      localStorage.setItem("sv_ingest_files", JSON.stringify(serializable));
    } catch {}
  };

  const setFilesAndPersist = (updater) => {
    setFiles(prev => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      persistFiles(next);
      return next;
    });
  };

  const updateFile = useCallback((id, updates) => {
    setFilesAndPersist(prev => prev.map(f => f.id === id ? { ...f, ...updates } : f));
  }, []);

  const handleFilesSelected = async (selectedFiles) => {
    const newFiles = Array.from(selectedFiles).map(f => ({
      id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 6)}`,
      file: f, name: f.name, size: f.size,
      ext: f.name.rsplit ? f.name.split(".").pop().toLowerCase() : f.name.split(".").pop()?.toLowerCase() || "unknown",
      status: "idle", progress: 0, documentType: null, confidence: 0,
      preview: null, uploadId: null, errors: [], importResult: null,
    }));
    setFilesAndPersist(prev => [...prev, ...newFiles]);
    setActiveView("queue");

    // Process each file independently
    for (const fileEntry of newFiles) {
      await processFile(fileEntry);
    }
  };

  const processFile = async (fileEntry) => {
    // Validate file blob exists (won't exist for restored sessions)
    if (!fileEntry.file || !(fileEntry.file instanceof globalThis.File)) {
      updateFile(fileEntry.id, { status: "failed", progress: 0, errors: ["File data not available. Please re-select this file."], feedback: "File must be re-selected after page refresh." });
      return;
    }
    if (fileEntry.file.size === 0) {
      updateFile(fileEntry.id, { status: "failed", progress: 0, errors: ["File is empty (0 bytes)."], feedback: "Uploaded file contains no data." });
      return;
    }
    updateFile(fileEntry.id, { status: "uploading", progress: 20, feedback: `Uploading ${fileEntry.name} (${(fileEntry.file.size/1024).toFixed(0)}KB)...` });
    try {
      const fd = new FormData();
      fd.append("file", fileEntry.file, fileEntry.name);
      updateFile(fileEntry.id, { status: "parsing", progress: 50, feedback: "Extracting data..." });
      const res = await axios.post(`${API}/ingest/document/process`, fd, { headers, timeout: 60000 });
      const data = res.data;
      const confidence = data.classification?.confidence || 0;
      const totalRows = data.total_rows || 0;

      // Confidence thresholds: >0.85 ready, 0.5-0.85 review, <0.5 blocked
      let status;
      let feedback;
      if (data.has_error) {
        status = "failed";
        feedback = data.error || "Could not extract data from this file.";
      } else if (confidence >= 0.85) {
        status = "ready";
        feedback = `Detected ${totalRows} rows as ${(data.classification?.type || "unknown").replace(/_/g, " ")}. Ready to import.`;
      } else if (confidence >= 0.5) {
        status = "needs_review";
        feedback = `Detected as ${(data.classification?.type || "unknown").replace(/_/g, " ")} (${(confidence * 100).toFixed(0)}% confidence). Please review before importing.`;
      } else if (totalRows > 0) {
        status = "needs_review";
        feedback = `Low confidence classification. ${totalRows} rows found. Manual review recommended.`;
      } else {
        status = "failed";
        feedback = "Could not extract usable data. Try a structured format (CSV, Excel) or review the file manually.";
      }

      updateFile(fileEntry.id, {
        status, progress: 100, feedback,
        documentType: data.classification?.type || "unknown",
        confidence, preview: data, uploadId: data.upload_id,
        errors: status === "failed" ? [feedback] : [],
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Upload failed. Please try again.";
      updateFile(fileEntry.id, { status: "failed", progress: 0, errors: [msg], feedback: msg });
    }
  };

  const confirmImport = async (fileEntry) => {
    if (!fileEntry.uploadId) return;
    updateFile(fileEntry.id, { status: "importing" });
    try {
      const res = await axios.post(`${API}/ingest/document/confirm/${fileEntry.uploadId}`, {}, { headers });
      updateFile(fileEntry.id, { status: "imported", importResult: res.data });
    } catch (err) {
      updateFile(fileEntry.id, { status: "failed", errors: [err.response?.data?.detail || "Import failed"] });
    }
  };

  const retryFile = async (fileEntry) => {
    updateFile(fileEntry.id, { status: "idle", errors: [], progress: 0 });
    await processFile(fileEntry);
  };

  const removeFile = (id) => setFilesAndPersist(prev => prev.filter(f => f.id !== id));

  const importAllReady = async () => {
    const ready = files.filter(f => f.status === "ready");
    for (const f of ready) { await confirmImport(f); }
  };

  const submitManual = async () => {
    setManualLoading(true);
    try {
      const data = { ...manualForm, amount: parseFloat(manualForm.amount) || 0, tax_amount: parseFloat(manualForm.tax_amount) || 0 };
      const endpoint = manualType === "invoice" ? "ingest/invoice" : "ingest/bill";
      const res = await axios.post(`${API}/${endpoint}`, data, { headers });
      setManualResult(res.data);
    } catch (e) { setManualResult({ error: e.response?.data?.detail || "Failed" }); }
    setManualLoading(false);
  };

  const readyCount = files.filter(f => f.status === "ready").length;
  const importedCount = files.filter(f => f.status === "imported").length;
  const failedCount = files.filter(f => f.status === "failed").length;
  const reviewCount = files.filter(f => f.status === "needs_review").length;

  return (
    <div className="space-y-6" data-testid="ingest-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Data Import</h1>
          <p className="text-sm text-slate-500 mt-0.5">Upload financial documents or create entries manually</p>
        </div>
        <Button onClick={() => { setManualOpen(true); setManualResult(null); setManualForm({ counterparty: "", amount: "", tax_amount: "0", reference: "", due_date: "", description: "" }); }} className="bg-amber-600 hover:bg-amber-500 text-slate-900 gap-2" data-testid="manual-entry-btn">
          <Plus className="w-4 h-4" /> Manual Entry
        </Button>
      </div>

      {/* Upload Zone — ref-based button + drag-and-drop */}
      <input ref={fileInputRef} type="file" multiple accept=".pdf,.csv,.xlsx,.xls,.tsv,.json,.txt,.docx"
        onChange={(e) => { if (e.target.files?.length) handleFilesSelected(e.target.files); e.target.value = ""; }}
        style={{ display: "none" }} data-testid="file-input" />
      <Card className="bg-slate-900/40 border-slate-800/50 border-dashed hover:border-cyan-500/30 transition-colors"
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => { e.preventDefault(); e.stopPropagation(); if (e.dataTransfer?.files?.length) handleFilesSelected(e.dataTransfer.files); }}>
        <CardContent className="p-8">
          <div className="flex flex-col items-center" data-testid="upload-zone">
            <div className="w-16 h-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-4">
              <Upload className="w-8 h-8 text-cyan-400" />
            </div>
            <span className="text-base text-slate-300 font-semibold mb-1">Upload Financial Files</span>
            <span className="text-sm text-slate-500 text-center max-w-md mb-2">
              Bank statements, invoices, bills, spreadsheets, PDFs, and structured data
            </span>
            <span className="text-xs text-slate-600 mb-4">Drag and drop or click below. Multiple files supported.</span>
            <div className="flex flex-wrap gap-1.5 justify-center mb-5">
              {["PDF", "CSV", "XLSX", "JSON", "TSV", "TXT", "DOCX"].map(fmt => (
                <span key={fmt} className="text-[9px] font-mono px-2 py-0.5 rounded-full border border-slate-700/40 text-slate-500">{fmt}</span>
              ))}
            </div>
            <button type="button" onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-slate-50 text-sm font-semibold transition-colors active:scale-[0.97]"
              data-testid="select-files-btn">
              <Upload className="w-4 h-4" /> Select Files
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Upload Queue */}
      {files.length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800/50" data-testid="upload-queue">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm text-slate-300 font-manrope">
                Upload Queue ({files.length} file{files.length !== 1 ? "s" : ""})
              </CardTitle>
              <div className="flex gap-2">
                {readyCount > 0 && (
                  <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500" onClick={importAllReady} data-testid="import-all-btn">
                    Import All Ready ({readyCount})
                  </Button>
                )}
              </div>
            </div>
            {/* Summary badges */}
            <div className="flex gap-2 mt-2">
              {importedCount > 0 && <Badge className="bg-emerald-500/15 text-emerald-400 text-[9px] border-none">{importedCount} Imported</Badge>}
              {readyCount > 0 && <Badge className="bg-cyan-500/15 text-cyan-400 text-[9px] border-none">{readyCount} Ready</Badge>}
              {reviewCount > 0 && <Badge className="bg-amber-500/15 text-amber-400 text-[9px] border-none">{reviewCount} Needs Review</Badge>}
              {failedCount > 0 && <Badge className="bg-red-500/15 text-red-400 text-[9px] border-none">{failedCount} Failed</Badge>}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {files.map(f => {
              const cfg = STATUS_CONFIG[f.status] || STATUS_CONFIG.idle;
              const Icon = FILE_ICONS[f.ext] || FileIcon;
              return (
                <div key={f.id} className={`p-3 rounded-xl border ${f.status === "failed" ? "border-red-500/20 bg-red-500/5" : f.status === "imported" ? "border-emerald-500/20 bg-emerald-500/5" : "border-slate-800/40 bg-slate-900/20"}`}
                  data-testid={`file-row-${f.id}`}>
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 ${cfg.color} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-200 font-semibold truncate">{f.name}</span>
                        <span className="text-[9px] text-slate-600 font-mono">{(f.size / 1024).toFixed(0)}KB</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`text-[8px] ${cfg.bg} ${cfg.color} border-none`}>{cfg.label}</Badge>
                        {f.documentType && f.documentType !== "unknown" && (
                          <span className="text-[9px] text-slate-500">{f.documentType.replace(/_/g, " ")}</span>
                        )}
                        {f.confidence > 0 && (
                          <span className={`text-[9px] font-mono ${f.confidence >= 0.7 ? "text-emerald-400" : f.confidence >= 0.4 ? "text-amber-400" : "text-red-400"}`}>
                            {(f.confidence * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {/* Progress bar */}
                      {(f.status === "uploading" || f.status === "parsing") && (
                        <div className="mt-1.5">
                          <div className="w-full h-1.5 bg-slate-800 rounded-full">
                            <div className="h-full bg-cyan-400 rounded-full transition-all duration-500" style={{ width: `${f.progress}%` }} />
                          </div>
                          {f.feedback && <p className="text-[9px] text-cyan-400 mt-0.5">{f.feedback}</p>}
                        </div>
                      )}
                      {/* Feedback for ready/review states */}
                      {f.feedback && f.status !== "uploading" && f.status !== "parsing" && f.status !== "failed" && f.status !== "imported" && (
                        <p className="text-[9px] text-slate-500 mt-1">{f.feedback}</p>
                      )}
                      {/* Errors */}
                      {f.errors.length > 0 && (
                        <div className="mt-1.5 p-2 rounded-lg bg-red-500/10 border border-red-500/15">
                          <p className="text-[10px] text-red-400">{f.errors[0]}</p>
                        </div>
                      )}
                      {/* Import result */}
                      {f.importResult && (
                        <div className="mt-2 rounded-xl bg-emerald-500/[0.03] border border-emerald-500/15 overflow-hidden">
                          {/* Success header */}
                          <div className="p-3 border-b border-emerald-500/10 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            <span className="text-xs text-emerald-400 font-semibold">Data Successfully Imported</span>
                          </div>
                          {/* Stats */}
                          <div className="p-3 space-y-1">
                            <div className="text-xs text-slate-400">
                              {f.importResult.imported} records parsed as {f.importResult.doc_type?.replace(/_/g, " ")}
                            </div>
                            {f.importResult.pooled_count > 0 && (
                              <div className="flex items-center gap-2">
                                <DatabaseZap className="w-3.5 h-3.5 text-cyan-400" />
                                <span className="text-xs text-cyan-400 font-semibold">{f.importResult.pooled_count} records added to AI Data Pool</span>
                              </div>
                            )}
                            {f.importResult.pooled_count === 0 && f.importResult.imported > 0 && (
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                                <span className="text-xs text-amber-400">Warning: records parsed but 0 added to pool.</span>
                              </div>
                            )}
                            {/* Microcopy */}
                            <p className="text-[10px] text-slate-600 mt-1">
                              Your data has not yet been analysed. Run the AI Diagnostic to understand what belongs to you, what is obligated, and how funds should be allocated.
                            </p>
                          </div>
                          {/* CTAs — MANDATORY */}
                          <div className="p-3 pt-0 flex gap-2">
                            <Button size="sm" className="h-7 text-xs bg-cyan-600 hover:bg-cyan-500 gap-1.5 font-semibold"
                              onClick={() => navigate(`/dashboard/data-pool`)}
                              data-testid={`run-diagnostic-${f.id}`}>
                              <BrainCircuit className="w-3.5 h-3.5" /> Run AI Diagnostic
                            </Button>
                            <Button size="sm" variant="outline" className="h-7 text-xs border-slate-700 text-slate-400 hover:text-slate-200 gap-1.5"
                              onClick={() => navigate(`/dashboard/data-pool`)}
                              data-testid={`open-pool-${f.id}`}>
                              <DatabaseZap className="w-3.5 h-3.5" /> Open AI Data Pool
                            </Button>
                          </div>
                        </div>
                      )}
                      {/* Preview rows count */}
                      {f.preview && f.status !== "imported" && (
                        <p className="text-[9px] text-slate-600 mt-1">{f.preview.total_rows} rows detected | {f.preview.columns?.length || 0} columns</p>
                      )}
                    </div>
                    {/* Actions */}
                    <div className="flex gap-1 shrink-0">
                      {f.status === "ready" && (
                        <Button size="sm" className="h-6 text-[9px] bg-emerald-600 hover:bg-emerald-500" onClick={() => confirmImport(f)}>
                          Import
                        </Button>
                      )}
                      {f.status === "needs_review" && (
                        <Button size="sm" variant="outline" className="h-6 text-[9px] border-amber-500/30 text-amber-400" onClick={() => setReviewFile(f)}>
                          <Eye className="w-3 h-3 mr-0.5" /> Review
                        </Button>
                      )}
                      {f.status === "failed" && (
                        <Button size="sm" variant="outline" className="h-6 text-[9px] border-slate-700" onClick={() => retryFile(f)}>
                          <RefreshCw className="w-3 h-3 mr-0.5" /> Retry
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-600 hover:text-red-400" onClick={() => removeFile(f.id)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewFile} onOpenChange={() => setReviewFile(null)}>
        {reviewFile && (
          <DialogContent className="bg-[#0B1120] border-slate-800 max-w-2xl" data-testid="review-dialog">
            <DialogHeader>
              <DialogTitle className="font-barlow text-slate-50 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
                Review: {reviewFile.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex gap-3">
                <Badge className="bg-amber-500/15 text-amber-400 text-[9px] border-none">
                  {reviewFile.documentType?.replace(/_/g, " ") || "Unknown"} ({(reviewFile.confidence * 100).toFixed(0)}% confidence)
                </Badge>
                <span className="text-xs text-slate-500">{reviewFile.preview?.total_rows || 0} rows | {reviewFile.preview?.format}</span>
              </div>
              {/* Preview table */}
              {reviewFile.preview?.preview_rows?.length > 0 && (
                <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800/50">
                        {(reviewFile.preview.columns?.length > 0 ? reviewFile.preview.columns : Object.keys(reviewFile.preview.preview_rows[0] || {})).map(c => (
                          <TableHead key={c} className="text-[9px] text-slate-500 whitespace-nowrap">{c}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reviewFile.preview.preview_rows.slice(0, 10).map((row, i) => (
                        <TableRow key={i} className="border-slate-800/30">
                          {(reviewFile.preview.columns?.length > 0 ? reviewFile.preview.columns : Object.keys(row)).map(c => (
                            <TableCell key={c} className="text-[10px] text-slate-400 whitespace-nowrap">{row[c] || ""}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              {/* Extracted fields */}
              {reviewFile.preview?.fields && Object.keys(reviewFile.preview.fields).length > 1 && (
                <div className="p-3 rounded-lg bg-slate-800/30 border border-slate-700/20">
                  <span className="text-[9px] text-slate-500 uppercase tracking-wider">Extracted Fields</span>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {Object.entries(reviewFile.preview.fields).filter(([k]) => k !== "extracted_rows" && k !== "doc_type").map(([k, v]) => (
                      <div key={k} className="flex justify-between text-xs">
                        <span className="text-slate-500">{k.replace(/_/g, " ")}</span>
                        <span className="text-slate-300 font-mono">{typeof v === "number" ? v.toLocaleString() : String(v)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" className="border-slate-700" onClick={() => setReviewFile(null)}>Close</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-500" onClick={() => {
                updateFile(reviewFile.id, { status: "ready" });
                setReviewFile(null);
              }}>
                Approve & Mark Ready
              </Button>
              <Button className="bg-cyan-600 hover:bg-cyan-500" onClick={() => {
                confirmImport(reviewFile);
                setReviewFile(null);
              }}>
                Import Now
              </Button>
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800" data-testid="manual-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50">Create {manualType === "invoice" ? "Invoice" : "Bill"}</DialogTitle>
          </DialogHeader>
          {!manualResult ? (
            <div className="space-y-3">
              <div className="flex gap-2 mb-2">
                <Button size="sm" variant={manualType === "invoice" ? "default" : "outline"} className={manualType === "invoice" ? "bg-emerald-600" : "border-slate-700"} onClick={() => setManualType("invoice")}>Invoice</Button>
                <Button size="sm" variant={manualType === "bill" ? "default" : "outline"} className={manualType === "bill" ? "bg-amber-600" : "border-slate-700"} onClick={() => setManualType("bill")}>Bill</Button>
              </div>
              <div><Label className="text-[10px] text-slate-500">Counterparty</Label><Input value={manualForm.counterparty} onChange={e => setManualForm(p => ({...p, counterparty: e.target.value}))} className="bg-slate-900/50 border-slate-700 mt-1" data-testid="manual-counterparty" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-slate-500">Amount</Label><Input type="number" value={manualForm.amount} onChange={e => setManualForm(p => ({...p, amount: e.target.value}))} className="bg-slate-900/50 border-slate-700 mt-1" data-testid="manual-amount" /></div>
                <div><Label className="text-[10px] text-slate-500">Tax Amount</Label><Input type="number" value={manualForm.tax_amount} onChange={e => setManualForm(p => ({...p, tax_amount: e.target.value}))} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-[10px] text-slate-500">Reference</Label><Input value={manualForm.reference} onChange={e => setManualForm(p => ({...p, reference: e.target.value}))} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
                <div><Label className="text-[10px] text-slate-500">Due Date</Label><Input type="date" value={manualForm.due_date} onChange={e => setManualForm(p => ({...p, due_date: e.target.value}))} className="bg-slate-900/50 border-slate-700 mt-1" /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setManualOpen(false)} className="border-slate-700">Cancel</Button>
                <Button onClick={submitManual} disabled={manualLoading} className="bg-amber-600 hover:bg-amber-500 text-slate-900" data-testid="manual-submit">
                  {manualLoading ? "Creating..." : `Create ${manualType === "invoice" ? "Invoice" : "Bill"}`}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              {manualResult.error ? (
                <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5 text-sm text-red-400">{manualResult.error}</div>
              ) : (
                <div className="p-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5">
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /><span className="text-sm text-emerald-400">{manualType} created: {manualResult.reference}</span></div>
                </div>
              )}
              <Button onClick={() => { setManualOpen(false); setManualResult(null); }} className="w-full bg-slate-800 hover:bg-slate-700">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
